import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PaginationProps } from "@findemdev/maui";
// api
// components + styled components
import { Container, TableWrapper } from "./CompanyLists.styled";
import Filters from "./Filters/Filters";
import Header from "./Header/Header";
// context
import UserContext from "../../../../contexts/UserContext";
import Table from "./Table/Table";
import usePopulateCompanyList from "../../hooks/usePopulateCompanyList";
import {
  ALL_COMPANY_LISTS_PAGE_SIZE,
  CompanyListOwnerFilterValue,
  QUERY_PARAMS,
} from "../../common/constants";
import { ISearchFilter } from "../../../../types/shared";
import { useFetchAllUsersQuery } from "../../../../services/payments";

const CompanyLists = () => {
  // context
  const userModel = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchFilter, setSearchFilter] = useState<ISearchFilter[]>();
  const ownerFilter =
    searchParams.get(QUERY_PARAMS.OWNER) ?? CompanyListOwnerFilterValue.ALL;
  const targetUserId = searchParams.get(QUERY_PARAMS.TARGET_USER_ID);
  const currentUserId = userModel.getUser().uid;

  const { data: allUsers } = useFetchAllUsersQuery(
    { joid: currentUserId },
    { skip: !targetUserId || !userModel.getUser().uid }
  );

  const effectiveJoid = userModel.getEffectiveJoid(targetUserId);
  const effectiveAccountId = useMemo(
    () => userModel.getEffectiveAccountId(targetUserId, allUsers),
    [targetUserId, allUsers, userModel]
  );

  const page = useMemo(() => {
    const parsed = Number(searchParams.get("page"));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }, [searchParams]);

  const allFilters = useMemo(() => {
    const filters: ISearchFilter[] = [];

    if (searchFilter && searchFilter.length > 0) {
      filters.push(...searchFilter);
    }

    if (ownerFilter === CompanyListOwnerFilterValue.MY) {
      filters.push({
        field: 'uid',
        operator: 'eq',
        value: effectiveJoid,
      });
    } else if (ownerFilter === CompanyListOwnerFilterValue.SHARED) {
      filters.push({
        field: 'collaborators.entity_id',
        operator: 'contains',
        value: effectiveAccountId,
      });
    }

    return filters.length > 0 ? filters : undefined;
  }, [searchFilter, ownerFilter, effectiveJoid, effectiveAccountId]);

  const {
    allCompanyLists,
    isCompanyListsError,
    isCompanyListsFetching,
    isCompanyDetailsFetching,
    companyDetailsByMetaRef,
    shortlistedCompanyMetaRefs,
  } = usePopulateCompanyList({
    joid: effectiveJoid,
    pageNumber: page - 1,
    pageSize: ALL_COMPANY_LISTS_PAGE_SIZE,
    filters: allFilters,
  });

  // Filter out owned lists from "Shared with Me"
  const filteredCompanyLists = useMemo(() => {
    if (!allCompanyLists || ownerFilter !== CompanyListOwnerFilterValue.SHARED) {
      return allCompanyLists;
    }

    const filteredData = allCompanyLists.data?.filter(
      (list) => list.uid !== effectiveJoid
    );

    return {
      ...allCompanyLists,
      data: filteredData,
      count: filteredData?.length ?? 0,
    };
  }, [allCompanyLists, ownerFilter, effectiveJoid]);

  const handleOnPageChange: PaginationProps["onChange"] = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (page <= 1) {
          next.delete(QUERY_PARAMS.PAGE);
        } else {
          next.set(QUERY_PARAMS.PAGE, String(page));
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const handleOwnerFilterChange = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === CompanyListOwnerFilterValue.ALL) {
            next.delete(QUERY_PARAMS.OWNER);
          } else {
            next.set(QUERY_PARAMS.OWNER, value);
          }
          // owner change resets pagination
          next.delete(QUERY_PARAMS.PAGE);
          return next;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const searchValue = searchFilter?.[0]?.value ?? "";
  const prevSearchValueRef = useRef<string>(searchValue);

  useEffect(() => {
    // Reset pagination only when search value changes (avoid wiping page on mount)
    if (prevSearchValueRef.current === searchValue) {
      return;
    }
    prevSearchValueRef.current = searchValue;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(QUERY_PARAMS.PAGE);
        return next;
      },
      { replace: false }
    );
  }, [searchValue, setSearchParams]);

  return (
    <Container>
      <Header />
      <Filters
        count={filteredCompanyLists?.count}
        setSearchFilter={setSearchFilter}
        ownerFilter={ownerFilter}
        setOwnerFilter={handleOwnerFilterChange}
        selectedUserId={effectiveJoid}
      />
      <TableWrapper>
        <Table
          data={filteredCompanyLists?.data}
          currentPage={page}
          totalPages={filteredCompanyLists?.count ?? 0}
          handleOnPageChange={handleOnPageChange}
          isCompanyDetailsFetching={isCompanyDetailsFetching}
          shortlistedCompanyMetaRefs={shortlistedCompanyMetaRefs}
          companyDetailsByMetaRef={companyDetailsByMetaRef}
          isError={isCompanyListsError}
          isFetching={isCompanyListsFetching}
        />
      </TableWrapper>
    </Container>
  );
};

export default CompanyLists;
